import { useMemo, useState } from 'react'
import { DEFECTS, defectiveRecords } from '../../lib/championshipAudit.js'
import { api } from '../../lib/api.js'
import styles from './DefectList.module.css'

/* =========================================================================
 * THE DEFECT WORKLIST — see the issues, review them, change them.
 *
 * Brian, looking at the integrity page on his phone: "What am I supposed to do
 * with this. There isnt a call to action. It doesn't allow me to see the issues
 * or to review or change them."
 *
 * The page reported six numbers and a table of more numbers. Every one was
 * TRUE and none was ACTIONABLE: "288 no championship key" tells you a problem
 * exists and nothing about which records have it, so there was nowhere to click
 * and nothing to fix. A number you cannot open is a report, not a tool.
 *
 * So: pick a defect, see the actual records, and act on them.
 *
 * ⚠️ AND WHERE THERE IS NO HONEST FIX, THERE IS NO BUTTON. A missing DATE
 * cannot be derived from the record — a wrong date is worse than a blank one,
 * because it puts a championship in the wrong countdown bucket and a rep works
 * it at the wrong time. Those rows open in the Command Center to be worked by a
 * person. Offering "fix it" there would be offering to guess.
 * ========================================================================= */

const PAGE = 50

export default function DefectList({ records = [], defect, state = null, onClose }) {
  const [page, setPage] = useState(1)
  const [busy, setBusy] = useState(false)
  const [result, setResult] = useState(null)
  const [error, setError] = useState('')

  const meta = DEFECTS.find((d) => d.id === defect)
  const { rows, matched, capped, limit } = useMemo(
    () => defectiveRecords(records, { defect, state, limit: 2000 }),
    [records, defect, state],
  )

  const shown = rows.slice(0, page * PAGE)

  /**
   * Run the fix for the records ON SCREEN.
   *
   * Scoped to `shown` rather than everything on purpose: the person clicking has
   * read these rows. A button that also changes 1,900 records they have not
   * seen is not the button they think they are pressing.
   */
  const runFix = async (dryRun) => {
    setBusy(true); setError(''); setResult(null)
    try {
      const ids = shown.map((r) => r.id).filter(Boolean)
      const only = meta?.fixable?.startsWith('repair-defects:') ? meta.fixable.split(':')[1] : null
      const r = meta?.fixable === 'backfill-key'
        ? await api.backfillKeys({ dryRun, limit: ids.length || 200 })
        : await api.repairDefects({ dryRun, only, ids })
      setResult({ dryRun, ...r })
    } catch (e) {
      /* Name what did NOT happen. "Failed" alone leaves somebody wondering
       * whether half of it landed. */
      setError(`${e?.message || e} — nothing was changed by this attempt.`)
    } finally { setBusy(false) }
  }

  return (
    <div className={styles.wrap}>
      <div className={styles.head}>
        <div>
          <h3 className={styles.h}>{meta?.label || defect}{state ? ` · ${state}` : ''}</h3>
          <p className={styles.cost}>{meta?.cost}</p>
        </div>
        <button type="button" className="btn" onClick={onClose}>Close</button>
      </div>

      <p className={styles.count}>
        <b>{matched.toLocaleString()}</b> record{matched === 1 ? '' : 's'}
        {capped && <> · showing the first {limit.toLocaleString()} — narrow by state to see the rest</>}
      </p>

      {/* THE CALL TO ACTION, or an honest statement that there is not one. */}
      {meta?.fixable ? (
        <div className={styles.actions}>
          <button type="button" className="btn" disabled={busy || !shown.length} onClick={() => runFix(true)}>
            {busy ? 'Working…' : `Preview the fix on these ${shown.length}`}
          </button>
          <button type="button" className="btn-primary" disabled={busy || !shown.length} onClick={() => runFix(false)}>
            {meta.fixLabel} — {shown.length} record{shown.length === 1 ? '' : 's'}
          </button>
        </div>
      ) : (
        <p className={styles.noFix}>
          <strong>No automatic fix, deliberately.</strong> This cannot be derived from the
          record, and a wrong value is worse than a blank one — a blank still reads as
          missing. Open a record to work it.
        </p>
      )}

      {error && <p className={styles.bad}>{error}</p>}
      {result && (
        <p className={styles.ok}>
          {result.dryRun ? 'Preview — nothing written. ' : 'Applied. '}
          {result.written != null && <>{result.written} changed · </>}
          {result.matched != null && <>{result.matched} matched · </>}
          {result.planned != null && <>{result.planned} would change · </>}
          {result.collisions ? <>{result.collisions} left alone as duplicates · </> : null}
          {(result.errors || []).length ? `${result.errors.length} error(s)` : 'no errors'}
          {!result.dryRun && ' — reload to see the updated counts.'}
        </p>
      )}

      <div className={styles.scroll}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th className={styles.sticky}>Championship</th>
              <th>State</th><th>Sport</th><th>Season</th><th>Also wrong</th><th />
            </tr>
          </thead>
          <tbody>
            {shown.map((r) => (
              <tr key={r.id}>
                <th className={styles.sticky} scope="row">{r.name}</th>
                <td>{r.state || <span className={styles.gap}>—</span>}</td>
                <td>{r.sport || <span className={styles.gap}>none</span>}</td>
                <td>{r.season || <span className={styles.gap}>—</span>}</td>
                <td className={styles.chips}>
                  {r.defects.filter((d) => d !== defect).map((d) => (
                    <span key={d} className={styles.chip}>{DEFECTS.find((x) => x.id === d)?.label || d}</span>
                  ))}
                </td>
                <td>
                  {/* Every row is openable. Even a defect with no automatic fix
                    * has an action: go and work the record. */}
                  <a className={styles.open} href={`/command-center?record=${encodeURIComponent(r.id)}`}>Open ›</a>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {shown.length < rows.length && (
        <button type="button" className="btn" onClick={() => setPage((p) => p + 1)}>
          Show {Math.min(PAGE, rows.length - shown.length)} more — {rows.length - shown.length} still hidden
        </button>
      )}
    </div>
  )
}
